import type { Carer } from '@lmc/ts-common'
import _ from 'lodash'

export type SortKey =
  | 'recently-added'
  | 'first-added'
  | 'first-name-asc'
  | 'first-name-desc'

export const DEFAULT_SORT: SortKey = 'recently-added'

// Recency as epoch ms. Prefer the explicit `createdOn` — it's the source of
// truth (the backoffice can edit it) — but legacy accounts predate the field
// and have none. For those, fall back to the timestamp embedded in the Mongo
// ObjectId `id` (its first 4 bytes are the creation time in seconds), so every
// carer is sortable and newly-added members aren't buried (CM-3875).
const recencyOf = (carer: Carer): number => {
  const created = carer.createdOn
    ? new Date(carer.createdOn).getTime()
    : Number.NaN
  if (!Number.isNaN(created)) {
    return created
  }
  const id = carer.id
  if (typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id)) {
    return Number.parseInt(id.slice(0, 8), 16) * 1000
  }
  return 0
}

export const sortCarers = (carers: Carer[], sortKey: SortKey): Carer[] => {
  switch (sortKey) {
    case 'recently-added':
      return _.orderBy(carers, recencyOf, 'desc')
    case 'first-added':
      return _.orderBy(carers, recencyOf, 'asc')
    case 'first-name-asc':
      return _.orderBy(
        carers,
        (c) => (c.name?.first ?? '').toLowerCase(),
        'asc'
      )
    case 'first-name-desc':
      return _.orderBy(
        carers,
        (c) => (c.name?.first ?? '').toLowerCase(),
        'desc'
      )
    default: {
      const _exhaustive: never = sortKey
      return _exhaustive
    }
  }
}
